import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Checkbox, Stack } from "@mui/material";
import { BACKEND_URL } from "../../config/env";
import AudioRecorder from "../AudioRecorder";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
}));

const FlowBuilderAddAudioModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(false);
  const [preview, setPreview] = useState();
  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilderModals.singleBlockModal.titleAdd"),
    btn: i18n.t("flowBuilderModals.singleBlockModal.buttonAdd")
  });
  const [medias, setMedias] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({ title: i18n.t("flowBuilderModals.singleBlockModal.titleEdit"), btn: i18n.t("flowBuilderModals.singleBlockModal.buttonSave") });
      setPreview(BACKEND_URL + "/public/" + data.data.url);
      setRecord(data.data.record);
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({ title: i18n.t("flowBuilderModals.singleBlockModal.titleAdd"), btn: i18n.t("flowBuilderModals.singleBlockModal.buttonAdd") });
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const uploadAudio = async (formData) => {
    const { data: res } = await api.post("/flowbuilder/audio", formData);
    return res.name;
  };

  const handleSaveContact = async () => {
    if (open === "edit") {
      if (medias.length > 0) {
        try {
          setLoading(true);
          const formData = new FormData();
          formData.append("fromMe", true);
          medias.forEach(media => formData.append("medias", media));
          const newUrl = await uploadAudio(formData);
          onUpdate({ ...data, data: { url: newUrl, record } });
          toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
          handleClose();
        }
      } else {
        onUpdate({ ...data, data: { ...data.data, record } });
        handleClose();
      }
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("fromMe", true);
      medias.forEach(media => formData.append("medias", media));
      const newUrl = await uploadAudio(formData);
      onSave({ url: newUrl, record });
      toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setMedias([]);
      setPreview();
      handleClose();
    }
  };

  const handleChangeMedias = e => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 5000000) {
      toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.fileTooLarge5MB"));
      return;
    }
    const selectedMedias = Array.from(e.target.files);
    setPreview(URL.createObjectURL(e.target.files[0]));
    setMedias(selectedMedias);
  };

  const handleRecorded = (blob, file) => {
    setPreview(URL.createObjectURL(blob));
    setMedias([file]);
    setRecord(true);
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth="md" scroll="paper">
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack gap={"16px"}>
              {preview && (
                <Stack direction="row" justifyContent="center">
                  <audio controls style={{ width: "100%" }}>
                    <source src={preview} type="audio/mp3" />
                    {i18n.t("flowBuilderModals.singleBlockModal.validation.browserNotSupported")}
                  </audio>
                </Stack>
              )}

              {preview && (
                <Stack direction="row" justifyContent="center" alignItems="center">
                  <Checkbox
                    checked={record}
                    onChange={() => setRecord(old => !old)}
                  />
                  <Typography>{i18n.t("flowBuilderModals.singleBlockModal.fields.sendAsRecordedAudio")}</Typography>
                </Stack>
              )}

              {!loading && (
                <>
                  <Button
                    variant="contained"
                    component="label"
                    style={{
                      color: "white",
                      backgroundColor: "#ba8d1a",
                      boxShadow: "none",
                      borderRadius: 0
                    }}
                  >
                    {i18n.t("flowBuilderModals.singleBlockModal.buttons.sendAudio")}
                    <input
                      type="file"
                      accept="audio/ogg, audio/mp3"
                      hidden
                      onChange={handleChangeMedias}
                      disabled={loading}
                    />
                  </Button>

                  <AudioRecorder onRecorded={handleRecorded} />
                </>
              )}

              {loading && (
                <Stack justifyContent="center" alignSelf="center">
                  <CircularProgress />
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {!loading && (
              <>
                <Button
                  onClick={() => {
                    handleClose();
                    setMedias([]);
                    setPreview();
                  }}
                  color="secondary"
                  variant="outlined"
                >
                  {i18n.t("contactModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={loading}
                  onClick={handleSaveContact}
                >
                  {labels.btn}
                </Button>
              </>
            )}
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddAudioModal;
